// Lightweight privacy-first analytics tracker.
// - Anonymous: no user id sent to server.
// - session_id is generated client-side and kept in sessionStorage; rotates on
//   30-min inactivity.
// - visitor_id is a client-only hint (localStorage) so the server can flag new
//   vs returning locally; the server also derives its own daily-salted id.
// - Uses navigator.sendBeacon when available (fire-and-forget on unload).

const SESSION_KEY = "sg_session_id";
const SESSION_TS_KEY = "sg_session_last";
const VISITOR_KEY = "sg_visitor_id";
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

function _uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "s-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function _base() {
  return (typeof import.meta !== "undefined" && import.meta.env?.REACT_APP_BACKEND_URL) ||
         (typeof process !== "undefined" && process.env?.REACT_APP_BACKEND_URL) || "";
}

export function getSessionId() {
  try {
    const now = Date.now();
    let sid = sessionStorage.getItem(SESSION_KEY);
    const last = parseInt(sessionStorage.getItem(SESSION_TS_KEY) || "0", 10);
    if (!sid || (last && now - last > SESSION_TIMEOUT_MS)) {
      sid = _uuid();
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    sessionStorage.setItem(SESSION_TS_KEY, String(now));
    return sid;
  } catch (e) {
    return _uuid();
  }
}

export function ensureVisitor() {
  try {
    let vid = localStorage.getItem(VISITOR_KEY);
    const isNew = !vid;
    if (isNew) {
      vid = _uuid();
      localStorage.setItem(VISITOR_KEY, vid);
    }
    return { visitorId: vid, isNew };
  } catch (e) {
    return { visitorId: _uuid(), isNew: true };
  }
}

function _device() {
  const ua = navigator.userAgent || "";
  const w = window.innerWidth || 0;
  let device_type = "desktop";
  if (/Mobi|Android|iPhone/i.test(ua) && w < 768) device_type = "mobile";
  else if (/iPad|Tablet/i.test(ua) || (w >= 768 && w < 1024 && /Touch|Mobi|Android/i.test(ua))) device_type = "tablet";

  let browser = "other";
  if (/Edg\//.test(ua)) browser = "Edge";
  else if (/OPR\//.test(ua) || /Opera/.test(ua)) browser = "Opera";
  else if (/Chrome/.test(ua) && !/Edg\//.test(ua)) browser = "Chrome";
  else if (/Safari/.test(ua) && !/Chrome/.test(ua)) browser = "Safari";
  else if (/Firefox/.test(ua)) browser = "Firefox";

  let os = "other";
  if (/Windows NT/.test(ua)) os = "Windows";
  else if (/Mac OS X/.test(ua) && !/iPhone|iPad/.test(ua)) os = "macOS";
  else if (/Android/.test(ua)) os = "Android";
  else if (/iPhone|iPad|iPod/.test(ua)) os = "iOS";
  else if (/Linux/.test(ua)) os = "Linux";

  return { device_type, browser, os };
}

function _utm() {
  try {
    const p = new URLSearchParams(window.location.search);
    return {
      utm_source: p.get("utm_source") || "",
      utm_medium: p.get("utm_medium") || "",
      utm_campaign: p.get("utm_campaign") || "",
    };
  } catch (e) {
    return { utm_source: "", utm_medium: "", utm_campaign: "" };
  }
}

function _basePayload() {
  const { visitorId, isNew } = ensureVisitor();
  const dev = _device();
  return {
    session_id: getSessionId(),
    path: window.location.pathname,
    referrer: document.referrer || "",
    ...dev,
    language: (navigator.language || "").slice(0, 20),
    screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
    viewport: `${window.innerWidth || 0}x${window.innerHeight || 0}`,
    is_new_visitor: isNew,
    ..._utm(),
    metadata: { vid: visitorId },
  };
}

async function _send(payload) {
  try {
    const url = `${_base()}/api/analytics/track`;
    const body = JSON.stringify(payload);
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      const ok = navigator.sendBeacon(url, blob);
      if (ok) return;
    }
    await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      credentials: "omit",
    });
  } catch (e) {
    // Silent — analytics must never break the app.
  }
}

export function trackPageview() {
  const payload = { event_type: "pageview", ..._basePayload() };
  _send(payload);
}

export function trackEvent(eventType, metadata) {
  const payload = { event_type: eventType, ..._basePayload() };
  if (metadata) payload.metadata = { ...(payload.metadata || {}), ...metadata };
  _send(payload);
}

export const ANALYTICS_EVENTS = {
  SIGNUP: "signup",
  WAITLIST_SUBMIT: "waitlist_submit",
  CONTACT_SUBMIT: "contact_submit",
  BOOKING_CONFIRMED: "booking_confirmed",
  EVENT_RSVP: "event_rsvp",
};
